import ShareholderList from "../share-holder-list";
import { RoomConfigCard } from "../application-card-details";

/** A room class as returned by `LicenseRequestDTO.ResidenceInfo` — `totalRooms` is a string there. */
export type ResidenceInfo = {
    roomClass?: string;
    totalRooms?: string | number;
    price?: string | number;
};

interface ILicenceDetailsCardProps {
    titleHeader: string;
    licenseCategory?: string;
    accommodationCategory?: string;
    /** `accommodationType` on the licence — the enum name, e.g. HOTEL. */
    accommodationType?: string;
    tourismResidences?: string;
    totalRooms?: string | number;
    estimatedFee?: string | number;
    /** Bill currency; the licence carries it on `feeCurrency`. */
    feeCurrency?: string;
    /** Set once the licence is approved. */
    licenceNumber?: string;
    issuedDate?: string;
    expiryDate?: string;
    /** Room classes with their counts and prices; empty for every application
     *  until room details can be captured. */
    residences?: ResidenceInfo[];
    shareholders: Array<any>;
}

export default function LicenceDetailsCard({
    titleHeader,
    licenseCategory,
    accommodationCategory,
    accommodationType,
    tourismResidences,
    totalRooms,
    estimatedFee,
    feeCurrency = "TZS",
    licenceNumber,
    issuedDate,
    expiryDate,
    residences,
    shareholders
}: ILicenceDetailsCardProps) {

    return (
        <div>
            <h1 className=" text-lg font-semibold text-[#3C1E03]">{titleHeader}</h1>
            <div className="bg-white shadow-md p-6 rounded-xl mt-2">
                <div className="grid grid-cols-2 gap-4">

                    {licenseCategory && (
                        <div className="col-span-2">
                            <span className="text-md text-gray-500">License Category</span>
                            <p className="text-sm font-semibold capitalize">{licenseCategory}</p>
                        </div>
                    )}

                    {accommodationCategory && (
                        <div>
                            <span className="text-md text-gray-500">Accommodation Category</span>
                            <p className="text-sm font-semibold capitalize">{accommodationCategory}</p>
                        </div>
                    )}

                    {accommodationType && (
                        <div>
                            <span className="text-md text-gray-500">Accommodation Type</span>
                            <p className="text-sm font-semibold capitalize">{accommodationType.toLowerCase().replace(/_/g, " ")}</p>
                        </div>
                    )}

                    {tourismResidences && (
                        <div>
                            <span className="text-md text-gray-500">Tourism Residences</span>
                            <p className="text-md font-semibold capitalize">{tourismResidences}</p>
                        </div>
                    )}

                    {totalRooms !== undefined && (
                        <div>
                            <span className="text-md text-gray-500">Total Rooms</span>
                            <p className="text-sm font-semibold capitalize">{totalRooms}</p>
                        </div>
                    )}

                    {estimatedFee !== undefined && (
                        <div>
                            <span className="text-md text-gray-500">Estimated Fee</span>
                            <p className="text-sm font-semibold capitalize">{typeof estimatedFee === 'number' ? estimatedFee.toLocaleString() : estimatedFee} {feeCurrency}</p>
                        </div>
                    )}

                    {licenceNumber && (
                        <div>
                            <span className="text-md text-gray-500">Licence Number</span>
                            <p className="text-sm font-semibold">{licenceNumber}</p>
                        </div>
                    )}

                    {issuedDate && (
                        <div>
                            <span className="text-md text-gray-500">Issued On</span>
                            <p className="text-sm font-semibold">{issuedDate}</p>
                        </div>
                    )}

                    {expiryDate && (
                        <div>
                            <span className="text-md text-gray-500">Expires On</span>
                            <p className="text-sm font-semibold">{expiryDate}</p>
                        </div>
                    )}

                </div>

                {residences && (
                    <div className="mt-4">
                        <span className="text-md text-gray-500">Room Configuration</span>
                        {residences.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                {residences.map((residence, index) => (
                                    <RoomConfigCard
                                        key={`${residence.roomClass ?? "room"}-${index}`}
                                        title={residence.roomClass ?? "Rooms"}
                                        total={`${residence.totalRooms ?? "—"}`}
                                        rate={residence.price !== undefined && residence.price !== null ? `${residence.price}` : "—"}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 mt-1">
                                No room details recorded for this application.
                            </p>
                        )}
                    </div>
                )}

                {shareholders && shareholders.length > 0 && (
                    <div className="flex flex-col gap-2 mt-2">
                        <ShareholderList
                            showActionButtons={false}
                            shareholders={shareholders}
                            onEdit={()=>{}}
                            onRemove={()=>{}}
                        />
                    </div>
                )}

            </div>
        </div>

    );
}
